<script>
  import GroupSelect from '@/components/GroupSelect/index.vue';
  import { billingEnumMixins } from '@/mixins/billingEnumMixins';
  import { financeInfoMixins } from '@/mixins/financeInfoMixins';
  import { detailBillingAdd, detailBillingUpdate } from 'api/billing/detail';
  import { roleUserMixins } from '@/mixins/roleUserMixins';
  import optionData from '@/assets/js/optionData';
  import { IntegrationType } from '@/assets/js/constants';
  import { checkStrNA, numToPercentStr } from 'utils/string';
  import { checkNumNA, isValid } from 'utils/number';
  import { getOfferDetail } from 'api/offer/detail';

  export default {
    name: 'BillingDetailBaseForm',
    components: { GroupSelect },
    mixins: [billingEnumMixins, financeInfoMixins, roleUserMixins],
    props: {
      addDetail: {
        type: Object,
        default: () => {
          return {};
        },
      },
      baseFormVisible: {
        type: Boolean,
        default: false,
      },
      baseFormType: {
        type: String,
        default: 'new',
      },
    },
    data() {
      return {
        formData: {},
        formProcessing: false,
        timezoneOption: optionData.timezoneOption,
        integrationType: IntegrationType,
      };
    },
    computed: {
      offerId: {
        get() {
          return this.formData.offerId;
        },
      },
    },
    watch: {
      addDetail: {
        handler(newVal) {
          this.formData = newVal;
        },
        deep: true,
      },
      offerId: {
        handler(newVal, oldVal) {
          if (newVal !== oldVal && !this.formProcessing && this.baseFormType === 'new') {
            setTimeout(() => {
              if (this.formData.offerId !== oldVal && !this.formProcessing) {
                this.fetchOfferInfo();
              }
            }, 300);
          }
        },
      },
    },
    methods: {
      checkNumNA,
      numToPercentStr,
      checkStrNA,
      fetchOfferInfo() {
        if (!isValid(this.formData.offerId)) {
          return;
        }
        this.formProcessing = true;
        getOfferDetail(this.formData.offerId)
          .then((res) => {
            this.formData.pkgName = res.result.prod;
            this.formData.offerName = res.result.offerName;
          })
          .finally(() => {
            setTimeout(() => {
              this.formProcessing = false;
            }, 300);
          });
      },
      handleAdvertiserChange() {
        const source = this.originSourceList.find((item) => item.id === this.formData.advertiserId);
        this.formData.advertiserName = source.company;
        this.formData.advertiserPm = source.pmName;
        this.formData.advertiserBd = source.bdName;
        this.formData.advCompanyPlatform = source.companyPlatform;
        this.formData.advBfiId = source.bfiId;
      },
      handleAffiliateChange() {
        const affiliate = this.affiliates.find((item) => item.id === this.formData.affiliateId);
        this.formData.affiliateAm = affiliate.amName;
        this.formData.affCompanyPlatform = affiliate.companyPlatform;
        this.formData.affBfiId = affiliate.bfiId;
      },
      handleCloseDialog() {
        this.$emit('update:baseFormVisible', false);
        this.$refs.baseForm.resetFields();
      },
      addDetailBilling() {
        if (this.formData.id === null) {
          console.log(this.formData);
        }
        for (let i = 0; i < this.affiliatesGroupList.length; i++) {
          for (let j = 0; j < this.affiliatesGroupList[i].children.length; j++) {
            if (this.affiliatesGroupList[i].children[j].id === this.formData.affiliateId) {
              this.formData.affiliateName = this.affiliatesGroupList[i].children[j].label.replace(
                this.formData.affiliateId + ' - ',
                ''
              );
              break;
            }
          }
        }
        this.formProcessing = true;
        if (this.formData.id !== undefined) {
          detailBillingUpdate(this.formData)
            .then(() => {
              this.formData = {};
              this.$message.success('修改成功');
              this.handleCloseDialog();
              this.$emit('refresh-list');
            })
            .finally(() => {
              this.formProcessing = false;
            });
        } else {
          detailBillingAdd(this.formData)
            .then(() => {
              this.formData = {};
              this.$message.success('添加成功');
              this.handleCloseDialog();
              this.$emit('refresh-list');
            })
            .finally(() => {
              this.formProcessing = false;
            });
        }
      },
    },
  };
</script>

<template>
  <el-dialog
    width="68%"
    :title="baseFormType === 'new' ? 'Add Detail Billing' : 'Edit Detail Billing'"
    :visible.sync="baseFormVisible"
    :close-on-click-modal="false"
    append-to-body
  >
    <el-form
      :model="formData"
      label-position="left"
      label-width="200px"
      :disabled="formProcessing"
      ref="baseForm"
    >
      <section class="grid-form">
        <el-form-item label="Date" prop="recordDate" required>
          <el-date-picker
            :disabled="formData.id !== undefined"
            style="width:250px;"
            v-model="formData.recordDate"
            format="yyyy-MM"
            value-format="yyyy-MM"
            type="month"
            align="right"
            unlink-panels
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="Timezone" required>
          <el-select v-model="formData.timezone" :disabled="formData.id !== undefined">
            <el-option
              v-for="item in timezoneOption"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            ></el-option>
          </el-select>
        </el-form-item>
      </section>

      <el-divider content-position="left">Offer Info</el-divider>

      <section class="grid-form">
        <el-form-item label="Advertiser Offer ID">
          <el-input
            :disabled="formData.id !== undefined"
            clearable
            v-model="formData.advertiserOfferId"
          />
        </el-form-item>
        <el-form-item label="Advertiser Offer Name">
          <el-input
            :disabled="formData.id !== undefined"
            clearable
            v-model="formData.advertiserOfferName"
          />
        </el-form-item>
        <el-form-item label="Agency Partner ID">
          <el-input
            :disabled="formData.id !== undefined"
            clearable
            v-model="formData.agencyPartnerId"
          />
        </el-form-item>
        <el-form-item label="Network Partner ID">
          <el-input
            :disabled="formData.id !== undefined"
            clearable
            v-model="formData.networkPartnerId"
          />
        </el-form-item>
        <el-form-item label="Offer ID" prop="offerId" required>
          <el-input
            :disabled="formData.id !== undefined"
            clearable
            type="number"
            v-model.number="formData.offerId"
          />
        </el-form-item>
        <el-form-item label="Offer Name" prop="offerName">
          {{ checkStrNA(formData.offerName) }}
        </el-form-item>
        <el-form-item label="PKG Name" prop="pkgName">
          {{ checkStrNA(formData.pkgName) }}
        </el-form-item>
        <el-form-item label="Country" prop="country" required>
          <el-input :disabled="formData.id !== undefined" clearable v-model="formData.country" />
        </el-form-item>
        <el-form-item label="Conversion">
          <el-input
            :disabled="formData.id !== undefined"
            clearable
            type="number"
            v-model.number="formData.conversion"
          />
        </el-form-item>
        <el-form-item label="Revenue">
          <el-input
            :disabled="formData.id !== undefined"
            clearable
            type="number"
            v-model.number="formData.revenue"
          />
        </el-form-item>
      </section>

      <el-divider content-position="left">Advertiser Info</el-divider>

      <section class="grid-form">
        <el-form-item label="Advertiser" prop="advertiserId" required>
          <group-select
            :disabled="formData.id !== undefined"
            v-model="formData.advertiserId"
            :data-source="sourcesList"
            :loading="sourceLoading"
            clearable
            reserve-keyword
            filterable
            placeholder="请选择"
            @change="handleAdvertiserChange"
          />
        </el-form-item>
        <el-form-item label="Company Platform">
          <span>{{ checkStrNA(formData.advCompanyPlatform) }}</span>
        </el-form-item>
        <el-form-item label="PM">
          <span>{{ checkStrNA(formData.advertiserPm) }}</span>
        </el-form-item>
        <el-form-item label="BD">
          <span>{{ checkStrNA(formData.advertiserBd) }}</span>
        </el-form-item>
        <el-form-item label="Partner Advertiser Account">
          <el-input
            clearable
            v-model="formData.partnerAdvertiserAccount"
            :disabled="baseFormType !== 'new'"
          />
        </el-form-item>
        <el-form-item label="Adv Integration Type">
          <el-select
            :disabled="formData.id !== undefined"
            clearable
            v-model="formData.advertiserIntegrationType"
            style="width:250px;"
          >
            <el-option
              v-for="item in integrationType"
              :key="item"
              :value="item"
              :label="item"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Adv Conversion">
          <el-input clearable type="number" v-model.number="formData.advertiserConversion" />
        </el-form-item>
        <el-form-item label="Adv Deduction Conversion">
          <el-input
            clearable
            type="number"
            v-model.number="formData.advertiserDeductionConversion"
          />
        </el-form-item>
        <el-form-item label="Adv Revenue">
          <el-input clearable type="number" v-model.number="formData.advertiserRevenue" />
        </el-form-item>
        <el-form-item label="Adv Confirmed Conversion" v-if="baseFormType === 'update'">
          <span>{{ formData.advertiserConfirmedConversion }}</span>
        </el-form-item>
        <el-form-item label="Adv Deduction Amount">
          <el-input clearable type="number" v-model.number="formData.advertiserDeductionAmount" />
        </el-form-item>
        <el-form-item label="Adv Cover Amount">
          <el-input clearable type="number" v-model.number="formData.advertiserCoverAmount" />
        </el-form-item>
        <el-form-item label="Adv Confirmed Amount" v-if="baseFormType === 'update'">
          <span>{{ formData.advertiserConfirmedAmount }}</span>
        </el-form-item>
        <el-form-item label="Adv Confirmed Status" v-if="baseFormType === 'update'">
          <span>{{
            checkStrNA(getEnumLabel(reconciliationProgressList, formData.advReconciliationProgress))
          }}</span>
        </el-form-item>
        <el-form-item label="Adv Payment Rate" v-if="baseFormType === 'update'">
          {{ numToPercentStr(formData.advPaymentRate) }}
        </el-form-item>
      </section>

      <el-divider content-position="left">Affiliate Info</el-divider>

      <section class="grid-form">
        <el-form-item label="Aff Integration Type">
          <el-select
            :disabled="formData.id !== undefined"
            clearable
            v-model="formData.affiliateIntegrationType"
            style="width:250px;"
          >
            <el-option
              v-for="item in integrationType"
              :key="item"
              :value="item"
              :label="item"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Affiliate" prop="affiliateId" required>
          <group-select
            :disabled="formData.id !== undefined"
            v-model="formData.affiliateId"
            :data-source="affiliatesGroupList"
            :loading="affiliateLoading"
            clearable
            filterable
            reserve-keyword
            style="width:250px;"
            @change="handleAffiliateChange"
          />
        </el-form-item>
        <el-form-item label="Company Platform">
          <span>{{ checkStrNA(formData.affCompanyPlatform) }}</span>
        </el-form-item>
        <el-form-item label="AM">
          <span>{{ checkStrNA(formData.affiliateAm) }}</span>
        </el-form-item>
        <el-form-item label="BD">
          <span>{{ checkStrNA(formData.affBd) }}</span>
        </el-form-item>
        <el-form-item label="Aff Conversion">
          <el-input clearable type="number" v-model.number="formData.affiliateConversion" />
        </el-form-item>
        <el-form-item label="Aff Deduction Conversion">
          <el-input
            clearable
            type="number"
            v-model.number="formData.affiliateDeductionConversion"
          />
        </el-form-item>
        <el-form-item label="Aff Confirmed Conversion" v-if="baseFormType === 'update'">
          <span>{{ formData.affiliateConfirmedConversion }}</span>
        </el-form-item>
        <el-form-item label="Aff Revenue">
          <el-input
            :disabled="formData.id !== undefined"
            clearable
            type="number"
            v-model.number="formData.affiliateRevenue"
          />
        </el-form-item>
        <el-form-item label="Aff Deduction Amount">
          <el-input clearable type="number" v-model.number="formData.affiliateDeductionAmount" />
        </el-form-item>
        <el-form-item label="Aff Cover Amount">
          <el-input clearable type="number" v-model.number="formData.affiliateCoverAmount" />
        </el-form-item>
        <el-form-item label="Aff Confirmed Amount" v-if="baseFormType === 'update'">
          <span>{{ formData.affiliateConfirmedAmount }}</span>
        </el-form-item>
        <el-form-item label="Aff Payment Rate" v-if="baseFormType === 'update'">
          {{ numToPercentStr(formData.affPaymentRate) }}
        </el-form-item>
        <el-form-item label="Aff Confirmed Status" v-if="baseFormType === 'update'">
          <span>{{
            checkStrNA(getEnumLabel(reconciliationProgressList, formData.affReconciliationProgress))
          }}</span>
        </el-form-item>
      </section>

      <el-divider content-position="left">Others</el-divider>
      <section class="grid-form">
        <el-form-item label="Estimated Profit" v-if="baseFormType === 'update'">
          {{ checkNumNA(formData.estimatedProfit) }}
        </el-form-item>
        <el-form-item label="Estimated Profit Rate" v-if="baseFormType === 'update'">
          {{ numToPercentStr(formData.estimatedProfitRate) }}
        </el-form-item>
        <el-form-item label="Note">
          <el-input clearable v-model="formData.note" />
        </el-form-item>
      </section>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleCloseDialog">取消</el-button>
      <el-button type="primary" @click="addDetailBilling()" :loading="formProcessing"
        >确定</el-button
      >
    </div>
  </el-dialog>
</template>

<style scoped lang="scss"></style>
